import React, { useCallback, useEffect, useState, Component } from 'react';
import { Search, Eye, X, ChevronLeft, ChevronRight, Loader2, AlertCircle, CheckCircle, AlertTriangle, Download, Filter, Smartphone, User } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAllAlerts, resolveAlert } from '../api/alerts';
// --- Types ---
interface Alert {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  alertType: string;
  message: string;
  status: 'active' | 'resolved';
  triggeredAt: string;
  resolvedAt?: string;
  user?: {
    id: string;
    fullName: string;
    email: string;
  };
  device?: {
    id: string;
    deviceUid: string;
  };
  sessionId?: string;
}
// --- Main Component ---
export function Alerts() {
  const {
    token
  } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters & Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  // Modals & Panels State
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [toasts, setToasts] = useState<{
    id: string;
    message: string;
    type: 'success' | 'error';
  }[]>([]);
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  // Fetch Data
  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const filters = {
        search: searchQuery,
        status: statusFilter === 'all' ? undefined : statusFilter,
        page: currentPage,
        limit
      };
      const res = await getAllAlerts(token, filters);
      setAlerts(res.alerts || []);
      setTotalAlerts(res.total || 0);
      // Fetch true active count from a separate query (not page-bound)
      try {
        const activeRes = await getAllAlerts(token, {
          status: 'active',
          limit: 1
        });
        setActiveCount(activeRes.total || 0);
      } catch {
        setActiveCount(0);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load alerts');
    } finally {
      setIsLoading(false);
    }
  }, [token, searchQuery, statusFilter, currentPage, limit]);
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, {
      id,
      message,
      type
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };
  const handleResolve = async (id: string) => {
    if (!token) return;
    try {
      await resolveAlert(token, id);
      showToast('Alert resolved');
      fetchAlerts();
    } catch (err: any) {
      showToast(err.message || 'Failed to resolve alert', 'error');
    }
  };
  const handleResolveAll = async () => {
    if (!token) return;
    try {
      // In a real app, call a bulk resolve endpoint
      const activeIds = alerts.filter(a => a.status === 'active').map(a => a.id);
      await Promise.all(activeIds.map(id => resolveAlert(token, id)));
      showToast('All active alerts resolved');
      fetchAlerts();
    } catch (err: any) {
      showToast(err.message || 'Failed to resolve alerts', 'error');
    }
  };
  const getRelativeTime = (timestamp: string) => {
    const diffMs = new Date().getTime() - new Date(timestamp).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };
  const formatAlertType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  return <div className="h-full flex flex-col" data-id="element-64">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6" data-id="element-65">
        <div className="flex items-center gap-4" data-id="element-66">
          <h1 className="text-2xl font-bold text-white" data-id="element-67">Alerts</h1>
          {activeCount > 0 && <span className="bg-red-500/10 text-red-500 px-3 py-1 rounded-full text-sm font-medium border border-red-500/20" data-id="element-68">
              {activeCount} Active
            </span>}
        </div>
        <button className="flex items-center justify-center gap-2 bg-[#6B21E8] hover:bg-[#5818c2] text-white px-4 py-2 rounded-lg transition-colors w-full sm:w-auto" data-id="element-69">
          <Download className="w-4 h-4" data-id="element-70" />
          Export Log
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6" data-id="element-71">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto flex-1" data-id="element-72">
          <div className="relative w-full sm:w-auto" data-id="element-73">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-74" />
            <input type="text" placeholder="Search alerts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full sm:w-[280px] bg-[#1A1A24] border border-gray-800 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-[#6B21E8] transition-colors" data-id="element-75" />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-76">
            <Filter className="w-5 h-5 text-gray-400" data-id="element-77" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-[#1A1A24] border border-gray-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#6B21E8] transition-colors w-full sm:w-auto" data-id="element-78">
              <option value="all" data-id="element-79">All Status</option>
              <option value="active" data-id="element-80">Active</option>
              <option value="resolved" data-id="element-81">Resolved</option>
              <option value="dismissed" data-id="element-82">Dismissed</option>
            </select>
          </div>
        </div>

        <div className="text-sm text-gray-400 hidden sm:block" data-id="element-83">
          Showing {(currentPage - 1) * 10 + 1}-
          {Math.min(currentPage * 10, totalAlerts)} of {totalAlerts}
        </div>
      </div>

      <div className="bg-[#1A1A24] border border-gray-800 rounded-xl flex-1 flex flex-col overflow-hidden" data-id="element-84">
        <div className="overflow-x-auto flex-1" data-id="element-85">
          <table className="w-full text-sm" data-id="element-86">
            <thead data-id="element-87">
              <tr className="border-b border-gray-800" data-id="element-88">
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-89">
                  Severity
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-90">
                  Message
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400 hidden md:table-cell" data-id="element-91">
                  Device
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400 hidden md:table-cell" data-id="element-92">
                  User
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-93">
                  Status
                </th>
                <th className="text-right py-4 px-4 font-medium text-gray-400" data-id="element-94">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-95">
              {isLoading ? <tr data-id="element-96">
                  <td colSpan={6} className="text-center py-8 text-gray-400" data-id="element-97">
                    <div className="flex items-center justify-center gap-2" data-id="element-98">
                      <div className="w-5 h-5 border-2 border-[#6B21E8] border-t-transparent rounded-full animate-spin" data-id="element-99" />
                      Loading alerts...
                    </div>
                  </td>
                </tr> : alerts.length === 0 ? <tr data-id="element-100">
                  <td colSpan={6} className="text-center py-8 text-gray-400" data-id="element-101">
                    No alerts found matching your criteria.
                  </td>
                </tr> : alerts.map(alert => {
              let severityColor = '';
              let severityBg = '';
              if (alert.severity === 'critical' || alert.severity === 'high') {
                severityColor = 'text-red-500';
                severityBg = 'bg-red-500/10';
              } else if (alert.severity === 'medium') {
                severityColor = 'text-yellow-500';
                severityBg = 'bg-yellow-500/10';
              } else {
                severityColor = 'text-green-500';
                severityBg = 'bg-green-500/10';
              }
              return <tr key={alert.id} className="h-[56px] border-b border-gray-800 hover:bg-gray-800/50 transition-colors" data-id="element-102">
                      <td className="py-4 px-4" data-id="element-103">
                        <div className="font-medium text-white" data-id="element-104">
                          {alert.message}
                        </div>
                        <div className="text-xs text-gray-400 mt-1" data-id="element-105">
                          {new Date(alert.createdAt).toLocaleString()}
                        </div>
                      </td>
                      <td className="py-4 px-4 hidden md:table-cell" data-id="element-106">
                        <div className="flex items-center gap-2" data-id="element-107">
                          <Smartphone className="w-4 h-4 text-gray-400" data-id="element-108" />
                          <span className="text-gray-300 font-mono text-xs" data-id="element-109">
                            {alert.deviceUid}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4 hidden md:table-cell" data-id="element-110">
                        <div className="flex items-center gap-2" data-id="element-111">
                          <User className="w-4 h-4 text-gray-400" data-id="element-112" />
                          <span className="text-gray-300" data-id="element-113">
                            {alert.userName}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4" data-id="element-114">
                        <div className="flex items-center gap-2" data-id="element-115">
                          <span className={`px-2 py-0.5 rounded-badge ${severityBg} ${severityColor} text-xs`} data-id="element-116">
                            {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4" data-id="element-117">
                        <div className="flex items-center gap-2" data-id="element-118">
                          <span className="text-xs text-gray-400" data-id="element-119">
                            {alert.status === 'active' ? 'Active' : 'Resolved'}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right" data-id="element-120">
                        <div className="flex items-center justify-end gap-2" data-id="element-121">
                          <button onClick={() => setSelectedAlert(alert)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-800/50 transition-colors text-gray-400 hover:text-white" data-id="element-122">
                            <Eye className="w-4 h-4" data-id="element-123" />
                          </button>
                          {alert.status === 'active' && <button onClick={() => handleResolve(alert.id)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-green-500/20 transition-colors text-green-500 hover:text-green-500" data-id="element-124">
                              <CheckCircle className="w-4 h-4" data-id="element-125" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-800" data-id="element-126">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400" data-id="element-127">
            <div className="sm:hidden" data-id="element-128">
              Showing {(currentPage - 1) * 10 + 1}-
              {Math.min(currentPage * 10, totalAlerts)} of {totalAlerts}
            </div>
            <div className="flex items-center gap-2" data-id="element-129">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-130">
                <ChevronLeft className="w-5 h-5" data-id="element-131" />
              </button>
              <span className="text-white font-medium" data-id="element-132">
                Page {currentPage} of {Math.ceil(totalAlerts / 10)}
              </span>
              <button onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalAlerts / 10), p + 1))} disabled={currentPage === Math.ceil(totalAlerts / 10)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-133">
                <ChevronRight className="w-5 h-5" data-id="element-134" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedAlert && <div className="fixed inset-y-0 right-0 z-50 flex" data-id="element-135">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedAlert(null)} data-id="element-136" />
          <div className="relative w-full max-w-[460px] h-full bg-[#1A1A24] border-l border-gray-800 flex flex-col shadow-2xl ml-auto" data-id="element-137">
            <div className="flex items-center justify-between p-6 border-b border-gray-800" data-id="element-138">
              <h2 className="text-xl font-bold text-white" data-id="element-139">Alert Details</h2>
              <button onClick={() => setSelectedAlert(null)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white" data-id="element-140">
                <X className="w-5 h-5" data-id="element-141" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-142">
              <div className="flex items-start gap-4" data-id="element-143">
                <div className={`w-12 h-12 rounded-full ${severityBg} flex items-center justify-center flex-shrink-0 mt-1`} data-id="element-144">
                  <AlertCircle className={`w-5 h-5 ${severityColor}`} data-id="element-145" />
                </div>
                <div data-id="element-146">
                  <div className="flex items-center gap-2 mb-1" data-id="element-147">
                    <span className={`px-2 py-0.5 rounded-badge ${severityBg} ${severityColor} text-xs uppercase tracking-wider font-bold`} data-id="element-148">
                      {alert.severity}
                    </span>
                    {alert.status === 'active' ? <span className="px-2 py-0.5 rounded-badge bg-red-500/20 text-red-500 text-xs uppercase tracking-wider font-bold" data-id="element-149">
                        Active
                      </span> : <span className="px-2 py-0.5 rounded-badge bg-green-500/20 text-green-500 text-xs uppercase tracking-wider font-bold" data-id="element-150">
                        Resolved
                      </span>}
                  </div>
                  <h3 className="text-lg font-semibold text-white" data-id="element-151">
                    {formatAlertType(alert.alertType)}
                  </h3>
                </div>
              </div>

              <div className="space-y-4" data-id="element-152">
                <h4 className="text-sm font-medium text-gray-400" data-id="element-153">Message</h4>
                <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4" data-id="element-154">
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-155">
                    {alert.message}
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-156">
                <h4 className="text-sm font-medium text-gray-400" data-id="element-157">Timing</h4>
                <div className="space-y-2" data-id="element-158">
                  <div className="flex justify-between" data-id="element-159">
                    <span className="text-xs text-gray-400" data-id="element-160">Triggered At</span>
                    <span className="text-xs text-gray-300" data-id="element-161">
                      {new Date(alert.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {alert.resolvedAt && <div className="flex justify-between" data-id="element-162">
                      <span className="text-xs text-gray-400" data-id="element-163">Resolved At</span>
                      <span className="text-xs text-gray-300" data-id="element-164">
                        {new Date(alert.resolvedAt).toLocaleString()}
                      </span>
                    </div>}
                </div>
              </div>

              <div className="space-y-4" data-id="element-165">
                <h4 className="text-sm font-medium text-gray-400" data-id="element-166">
                  Related Entities
                </h4>
                {alert.user && <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4" data-id="element-167">
                    <div className="flex justify-between items-start" data-id="element-168">
                      <div data-id="element-169">
                        <p className="text-xs text-gray-400 mb-1" data-id="element-170">User</p>
                        <p className="text-sm text-gray-300" data-id="element-171">
                          {alert.user.fullName}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-172">
                          {alert.user.email}
                        </p>
                      </div>
                      <button className="text-xs text-gray-400 hover:text-gray-200" data-id="element-173">
                        View
                      </button>
                    </div>
                  </div>}

                {alert.device && <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4" data-id="element-174">
                    <div className="flex justify-between items-start" data-id="element-175">
                      <div data-id="element-176">
                        <p className="text-xs text-gray-400 mb-1" data-id="element-177">Device</p>
                        <p className="text-sm text-gray-300 font-mono" data-id="element-178">
                          {alert.device.deviceUid}
                        </p>
                      </div>
                      <button className="text-xs text-gray-400 hover:text-gray-200" data-id="element-179">
                        View
                      </button>
                    </div>
                  </div>}

                {alert.sessionId && <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4" data-id="element-180">
                    <div className="flex justify-between items-start" data-id="element-181">
                      <div data-id="element-182">
                        <p className="text-xs text-gray-400 mb-1" data-id="element-183">Session</p>
                        <p className="text-sm text-gray-300 font-mono" data-id="element-184">
                          {alert.sessionId.substring(0, 8)}...
                        </p>
                      </div>
                      <button className="text-xs text-gray-400 hover:text-gray-200" data-id="element-185">
                        View Session
                      </button>
                    </div>
                  </div>}
              </div>
            </div>

            <div className="p-6 border-t border-gray-800" data-id="element-186">
              {alert.status === 'active' && <button onClick={() => handleResolve(alert.id)} className="w-full h-[40px] bg-[rgba(34,197,94,0.1)] text-green-500 border border-[rgba(34,197,94,0.2)] rounded-lg font-medium hover:bg-[rgba(34,197,94,0.2)] transition-colors flex items-center justify-center gap-2" data-id="element-187">
                  <CheckCircle className="w-4 h-4" data-id="element-188" />
                  Mark as Resolved
                </button>}
            </div>
          </div>
        </div>}

      {/* Toasts */}
      <div className="fixed top-20 right-4 sm:top-6 sm:right-6 z-50 flex flex-col gap-2" data-id="element-189">
        {toasts.map(toast => <div key={toast.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${toast.type === 'success' ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`} data-id="element-190">
            <div className={`w-2 h-2 rounded-full ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`} data-id="element-191" />
            <span className="text-sm font-medium" data-id="element-192">{toast.message}</span>
          </div>)}
      </div>
    </div>;
}
// --- Subcomponents ---
function AlertDetailPanel({
  alert,
  onClose,
  onResolve
}: {
  alert: Alert | null;
  onClose: () => void;
  onResolve: () => void;
}) {
  if (!alert) return null;
  let severityColor = '';
  let severityBg = '';
  if (alert.severity === 'critical' || alert.severity === 'high') {
    severityColor = 'text-danger';
    severityBg = 'bg-[rgba(239,68,68,0.15)]';
  } else if (alert.severity === 'medium') {
    severityColor = 'text-warning';
    severityBg = 'bg-[rgba(245,158,11,0.15)]';
  } else {
    severityColor = 'text-success';
    severityBg = 'bg-[rgba(34,197,94,0.15)]';
  }
  const formatAlertType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  return <div className="fixed inset-0 z-40 flex justify-end" data-id="element-193">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} data-id="element-194" />
      <div className="relative w-[420px] h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-250" data-id="element-195">
        {/* Header */}
        <div className="p-6 border-b border-border relative" data-id="element-196">
          <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-197">
            <X className="w-5 h-5" data-id="element-198" />
          </button>
          <div className="flex items-start gap-4 pr-8" data-id="element-199">
            <div className={`w-10 h-10 rounded-full ${severityBg} flex items-center justify-center flex-shrink-0 mt-1`} data-id="element-200">
              <AlertCircle className={`w-5 h-5 ${severityColor}`} data-id="element-201" />
            </div>
            <div data-id="element-202">
              <div className="flex items-center gap-2 mb-1" data-id="element-203">
                <span className={`px-2 py-0.5 rounded-badge ${severityBg} ${severityColor} text-[10px] uppercase tracking-wider font-bold`} data-id="element-204">
                  {alert.severity}
                </span>
                {alert.status === 'active' ? <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[10px] uppercase tracking-wider font-bold" data-id="element-205">
                    Active
                  </span> : <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[10px] uppercase tracking-wider font-bold" data-id="element-206">
                    Resolved
                  </span>}
              </div>
              <h2 className="text-[18px] font-semibold text-white" data-id="element-207">
                {formatAlertType(alert.alertType)}
              </h2>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-208">
          {/* Message */}
          <div data-id="element-209">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] mb-2" data-id="element-210">
              Message
            </h3>
            <div className="bg-background border border-border rounded-card p-4" data-id="element-211">
              <p className="text-[14px] text-white leading-relaxed" data-id="element-212">
                {alert.message}
              </p>
            </div>
          </div>

          {/* Timing */}
          <div className="space-y-4" data-id="element-213">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-214">
              Timing
            </h3>
            <div className="space-y-3" data-id="element-215">
              <div className="flex justify-between" data-id="element-216">
                <span className="text-[13px] text-text-secondary" data-id="element-217">
                  Triggered At
                </span>
                <span className="text-[13px] text-white" data-id="element-218">
                  {new Date(alert.triggeredAt).toLocaleString()}
                </span>
              </div>
              {alert.resolvedAt && <div className="flex justify-between" data-id="element-219">
                  <span className="text-[13px] text-text-secondary" data-id="element-220">
                    Resolved At
                  </span>
                  <span className="text-[13px] text-white" data-id="element-221">
                    {new Date(alert.resolvedAt).toLocaleString()}
                  </span>
                </div>}
            </div>
          </div>

          {/* Entities */}
          <div className="space-y-4" data-id="element-222">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-223">
              Related Entities
            </h3>

            {alert.user && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-224">
                <div data-id="element-225">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-226">User</p>
                  <p className="text-[13px] text-white" data-id="element-227">
                    {alert.user.fullName}
                  </p>
                  <p className="text-[11px] text-text-tertiary" data-id="element-228">
                    {alert.user.email}
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-229">
                  View
                </button>
              </div>}

            {alert.device && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-230">
                <div data-id="element-231">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-232">Device</p>
                  <p className="text-[13px] text-white font-mono" data-id="element-233">
                    {alert.device.deviceUid}
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-234">
                  View
                </button>
              </div>}

            {alert.sessionId && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-235">
                <div data-id="element-236">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-237">
                    Session
                  </p>
                  <p className="text-[13px] text-white font-mono" data-id="element-238">
                    {alert.sessionId.substring(0, 8)}...
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-239">
                  View Session
                </button>
              </div>}
          </div>
        </div>

        {/* Footer Actions */}
        {alert.status === 'active' && <div className="p-6 border-t border-border" data-id="element-240">
            <button onClick={onResolve} className="w-full h-[40px] bg-[rgba(34,197,94,0.1)] text-success border border-[rgba(34,197,94,0.2)] rounded-btn font-medium hover:bg-[rgba(34,197,94,0.2)] transition-colors flex items-center justify-center gap-2" data-id="element-241">
              <CheckCircle className="w-4 h-4" data-id="element-242" />
              Mark as Resolved
            </button>
          </div>}
      </div>
    </div>;
}